import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { LinkGroup, MainLink, SubLink, Count } from './';

interface MatchParams {
    accountId: string;
}
interface Link {
    path: string;
    label: React.ReactNode;
    count?: number;
}
interface MenuProps extends RouteComponentProps<MatchParams> {
    mainLink: Link;
    subLinks: Link[];
}

interface UnpackedUrlParams {
    [key: string]: string[];
}

const getQueryParamsDict = (queryString: string): UnpackedUrlParams => {
    const queryParams = new URLSearchParams(queryString);
    const entries = [...queryParams.entries()];
    return entries.reduce((acc: UnpackedUrlParams, [key, value]) => {
        if (key in acc) {
            acc[key] = [...acc[key], value].sort();
        } else {
            acc[key] = [value];
        }
        return acc;
    }, {});
};

const paramsAreEqual = (a: UnpackedUrlParams, b: UnpackedUrlParams) => {
    return JSON.stringify(a) === JSON.stringify(b);
};

const splitPathForComparison = (url: string) => {
    const split = url.split('?');
    const path = split[0];
    const query = split[1] || '';
    return [path, query];
};

const Menu = (
    {
        location,
        mainLink,
        subLinks,
    }: MenuProps
) => {
    const matchesMain = location.pathname.startsWith(mainLink.path);
    const currentQueryParamsDict = getQueryParamsDict(location.search.substr(1));
    const matchesSub = subLinks.find(subLink => {
        const [subPath, subQuery] = splitPathForComparison(subLink.path);
        const pathnameWithHash = `${location.pathname}${location.hash}`;
        if (pathnameWithHash === subPath) {
            return paramsAreEqual(currentQueryParamsDict, getQueryParamsDict(subQuery));
        }
        return false;
    });

    return (
        <LinkGroup>
            <MainLink to={mainLink.path} isActive={() => matchesMain && !matchesSub}>
                {mainLink.label}
                {mainLink.count && <Count>({mainLink.count})</Count>}
            </MainLink>
            {subLinks.map(subLink => (
                <SubLink
                    key={subLink.path}
                    to={subLink.path}
                    isActive={() => (matchesSub ? subLink.path === matchesSub.path : false)}
                >
                    {subLink.label}
                    {subLink.count && <Count>({subLink.count})</Count>}
                </SubLink>
            ))}
        </LinkGroup>
    );
};

export default withRouter(React.memo(Menu));
